import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';

const TOTAL_STEPS = 3;

const DeleteRecurringPaymentDialog = ({ visible, onHide, rowData, onConfirmDelete, loading }) => {
    const [step, setStep] = useState(1);
    const [ackAutoPayments, setAckAutoPayments] = useState(false);
    const [ackIrreversible, setAckIrreversible] = useState(false);
    const [confirmText, setConfirmText] = useState('');

    const policyNumber = rowData?.policyNumber || '';

    useEffect(() => {
        if (visible) {
            setStep(1);
            setAckAutoPayments(false);
            setAckIrreversible(false);
            setConfirmText('');
        }
    }, [visible, rowData]);

    if (!rowData) return null;

    const canProceedFromStep2 = ackAutoPayments && ackIrreversible;
    const canDelete = confirmText.trim().toUpperCase() === policyNumber.trim().toUpperCase();

    const handleClose = () => {
        if (loading) return;
        onHide();
    };

    const handleDeleteClick = async () => {
        if (!canDelete || loading) return;
        await onConfirmDelete();
    };

    const StepIndicator = () => (
        <div className="flex items-center justify-center gap-2 mb-6">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((s) => (
                <div key={s} className="flex items-center gap-2">
                    <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                            s === step
                                ? 'bg-error text-white'
                                : s < step
                                ? 'bg-error/20 text-error'
                                : 'bg-base-200 text-base-content/40'
                        }`}
                    >
                        {s < step ? <i className="pi pi-check"></i> : s}
                    </div>
                    {s < TOTAL_STEPS && (
                        <div className={`w-8 h-0.5 ${s < step ? 'bg-error/40' : 'bg-base-200'}`} />
                    )}
                </div>
            ))}
        </div>
    );

    return (
        <Dialog
            header="Delete Recurring Payment"
            visible={visible}
            onHide={handleClose}
            closable={!loading}
            style={{ width: '480px' }}
            breakpoints={{ '640px': '95vw' }}
        >
            <div className="p-2 text-center">
                <StepIndicator />

                {step === 1 && (
                    <div>
                        <div className="mx-auto bg-error/10 w-24 h-24 rounded-full flex items-center justify-center mb-6">
                            <i className="pi pi-exclamation-triangle text-error" style={{ fontSize: '3rem' }}></i>
                        </div>
                        <h3 className="font-bold text-xl mb-2 text-base-content">
                            Delete recurring payment for policy {policyNumber}?
                        </h3>
                        <ul className="text-left text-sm text-base-content/70 space-y-2 my-6 bg-base-200/60 rounded-2xl p-4">
                            <li className="flex gap-2">
                                <i className="pi pi-times-circle text-error mt-0.5"></i>
                                <span>Your premiums will <strong>no longer be collected automatically</strong>.</span>
                            </li>
                            <li className="flex gap-2">
                                <i className="pi pi-times-circle text-error mt-0.5"></i>
                                <span>You will be responsible for making <strong>manual payments</strong> before every due date.</span>
                            </li>
                            <li className="flex gap-2">
                                <i className="pi pi-times-circle text-error mt-0.5"></i>
                                <span>Missing a manual payment could result in your <strong>policy lapsing</strong>.</span>
                            </li>
                            <li className="flex gap-2">
                                <i className="pi pi-times-circle text-error mt-0.5"></i>
                                <span>This action is <strong>permanent and cannot be undone</strong>.</span>
                            </li>
                        </ul>
                        <div className="flex justify-center gap-4 mt-8">
                            <button type="button" className="btn rounded-full px-8 btn-ghost" onClick={handleClose}>
                                <i className="pi pi-times-circle text-lg mr-1"></i>
                                Cancel
                            </button>
                            <button type="button" className="btn rounded-full px-8 btn-error text-white" onClick={() => setStep(2)}>
                                Continue <i className="pi pi-arrow-right text-lg ml-1"></i>
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div>
                        <div className="mx-auto bg-error/10 w-24 h-24 rounded-full flex items-center justify-center mb-6">
                            <i className="pi pi-shield text-error" style={{ fontSize: '3rem' }}></i>
                        </div>
                        <h3 className="font-bold text-xl mb-2 text-base-content">Please confirm you understand</h3>
                        <p className="text-sm text-base-content/70 mb-6">
                            Read each statement carefully and tick both boxes to continue.
                        </p>
                        <div className="text-left space-y-4 mb-6">
                            <label className="flex items-start gap-3 p-3 rounded-2xl bg-base-200/60 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-error mt-0.5"
                                    checked={ackAutoPayments}
                                    onChange={(e) => setAckAutoPayments(e.target.checked)}
                                />
                                <span className="text-sm text-base-content">
                                    I understand that automatic payments for policy <strong>{policyNumber}</strong> will stop immediately after deletion.
                                </span>
                            </label>
                            <label className="flex items-start gap-3 p-3 rounded-2xl bg-base-200/60 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-error mt-0.5"
                                    checked={ackIrreversible}
                                    onChange={(e) => setAckIrreversible(e.target.checked)}
                                />
                                <span className="text-sm text-base-content">
                                    I understand this action is <strong>irreversible</strong> and I will need to re-enter my card details to set up recurring payments again if I change my mind.
                                </span>
                            </label>
                        </div>
                        <div className="flex justify-center gap-4 mt-8">
                            <button type="button" className="btn rounded-full px-8 btn-ghost" onClick={() => setStep(1)}>
                                <i className="pi pi-arrow-left text-lg mr-1"></i>
                                Back
                            </button>
                            <button
                                type="button"
                                className="btn rounded-full px-8 btn-error text-white"
                                disabled={!canProceedFromStep2}
                                onClick={() => setStep(3)}
                            >
                                Continue <i className="pi pi-arrow-right text-lg ml-1"></i>
                            </button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div>
                        <div className="mx-auto bg-error/10 w-24 h-24 rounded-full flex items-center justify-center mb-6">
                            <i className="pi pi-lock text-error" style={{ fontSize: '3rem' }}></i>
                        </div>
                        <h3 className="font-bold text-xl mb-2 text-base-content">Final confirmation</h3>
                        <p className="text-sm text-base-content/70 mb-4">
                            This is your last chance to back out. To permanently delete this recurring payment setup, type the policy number below exactly as shown.
                        </p>
                        <p className="text-sm font-bold text-base-content mb-4 tracking-wide">{policyNumber}</p>
                        <input
                            type="text"
                            className="input input-bordered w-full rounded-full text-center mb-6"
                            placeholder={`Type "${policyNumber}" to confirm`}
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            disabled={loading}
                            autoComplete="off"
                        />
                        <div className="flex justify-center gap-4 mt-8">
                            <button type="button" className="btn rounded-full px-8 btn-ghost" disabled={loading} onClick={() => setStep(2)}>
                                <i className="pi pi-arrow-left text-lg mr-1"></i>
                                Back
                            </button>
                            <button
                                type="button"
                                className="btn rounded-full px-8 btn-error text-white"
                                disabled={!canDelete || loading}
                                onClick={handleDeleteClick}
                            >
                                {loading ? <span className="loading loading-spinner"></span> : <i className="pi pi-trash text-lg mr-1"></i>}
                                {loading ? 'Deleting...' : 'Permanently Delete'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default DeleteRecurringPaymentDialog;
